import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';
import { API } from '../App';

/**
 * Homepage testimonials section. Auto-hides when zero approved testimonials
 * exist. Admin adds REAL testimonials via the admin dashboard — never seeded
 * with fake content.
 */
export default function TestimonialsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/testimonials?limit=6`);
        setItems(r.data.testimonials || []);
      } catch (_) {
        // silent — section auto-hides
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section className="py-16 md:py-20 bg-background" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 block">
            Real Stories
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-foreground">
            From Our Community
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t) => (
            <article
              key={t.testimonial_id}
              className="relative bg-muted/30 rounded-2xl p-6 border border-border/40"
              data-testid={`testimonial-${t.testimonial_id}`}
            >
              <Quote className="absolute top-4 right-4 h-5 w-5 text-primary/30" />

              {Number.isInteger(t.rating) && t.rating > 0 && (
                <div className="flex items-center mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={`s${t.testimonial_id}-${i}`}
                      className={`h-4 w-4 ${i < t.rating ? 'fill-primary text-primary' : 'text-muted'}`}
                    />
                  ))}
                </div>
              )}

              <p className="text-foreground/90 leading-relaxed text-sm mb-4">&ldquo;{t.quote}&rdquo;</p>

              <div className="flex items-center gap-3 pt-4 border-t border-border/40">
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold">
                    {t.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                  {t.role && (
                    <p className="text-xs text-muted-foreground truncate">{t.role}</p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
